import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ButtonWithIcon } from '@/components/atoms'
import { COLOR } from '@/utils/constants/colors'
import { SIZE } from '@/utils/constants/globalStyle'
import { ROUTES } from '@/utils/constants/routesConstants'
import { ButtonIcon } from 'pepsico-ds'

import { usePointMultiplierDownload } from './CreatePointMultiplier-hook'

const Divider = () => <div className="border-t border-gray-700" />

const renderField = (label, value, testId) => (
  <div className="flex flex-col gap-1" data-testid={testId}>
    <span className="text-sm text-[#EFEFEF]">{label}</span>
    <span className="mt-1 text-md font-bold text-white">{value || '-'}</span>
  </div>
)

const renderSectionHeader = (title, testId, editButton = null) => (
  <div className="mb-2 flex items-center justify-between" data-testid={testId}>
    <h2 className="text-md font-bold text-white">{title}</h2>
    {editButton}
  </div>
)

const renderFieldDownload = (label, fileName, testId, handleDownload) => (
  <div className="flex flex-col gap-1" data-testid={testId}>
    <span className="text-sm text-[#EFEFEF]">{label}</span>
    <span className="mt-1 text-md font-bold text-white flex items-center gap-2">
      {fileName ? (
        <>
          <span className="flex items-center">
            {fileName}
          </span>

          <a
            href={'#'}
            download={fileName}
            className="ml-2 text-[#3387DF] hover:none"
            aria-label="Download file"
            onClick={e => {
              e.preventDefault();
              handleDownload();
            }}
          >
            <ButtonIcon
              icon="file_download"
              isDark
              size="small"
              type="default"
              data-testid="file_download"
            />
          </a>
        </>
      ) : (
        '-'
      )}
    </span>
  </div>
)

export const ReviewPointMultiplierSection = ({ isReview, pointMultiplierId }) => {
  const navigate = useNavigate()

  const formData = useSelector(state => state.pointMultiplier)
  const displayData = {
    segment: formData?.userSegment || formData?.segment,
    category: formData?.productCategory || formData?.category,
    channel: formData?.orderChannel || formData?.channel,
  }
  const fileName = (pointMultiplierId
    ? formData?.metadata?.productFileName
    :   formData?.metadata?.productFileName || formData?.uploadOutcome?.fileName);
  const { handleDownload } = usePointMultiplierDownload({
    pointMultiplierId,
    formData,
    fileName
  });

  const handleEditSection = () => {
    navigate(ROUTES.POINT_MULTIPLIER_CREATE, {
      state: { formData },
    })
  }

  return (
    <div className="reviewPointMultiplierContent">
      <div className="flex flex-col gap-6" data-testid="review-point-multiplier-section">
        {/* Point Modifier Setup */}
        <div className="flex flex-col gap-4" data-testid="section-point-modifier">
          {renderSectionHeader(
            'Point Multiplier Setup',
            'header-point-modifier',
            isReview && (!formData?.status || formData?.status === 'draft') && (
              <ButtonWithIcon
                uiSchema={{
                  className: 'flex items-center ml-2 text-[#3387DF] font-bold text-sm leading-snug border-none',
                  title: 'Edit',
                  size: SIZE.SMALL,
                  variant: COLOR.TERTIARY,
                  isDark: true,
                  iconColor: '#3387DF',
                }}
                iconLeading="edit"
                handleButton={handleEditSection}
                id="edit-button"
              />
            )
          )}
          <Divider />
          {renderField('Multiplier Name', formData?.modifierName, 'field-modifier-name')}
        </div>

        {/* Rules */}
        <div className="flex flex-col gap-4" data-testid="section-rules">
          {renderSectionHeader('Rules', 'header-rules', null)}
          <Divider />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {renderField('Rules Criteria', formData?.rulesCriteria, 'field-rules-criteria')}
            {(formData?.rulesCriteria === 'Segment' || formData?.rulesCriteria === 'Products') && renderField('Segments', displayData.segment, 'field-segment')}
            {formData?.rulesCriteria === 'Category' && renderField('Category', displayData.category, 'field-category')}
            {formData?.rulesCriteria === 'Channel' && renderField('Channel', displayData.channel, 'field-channel')}
            {renderField('Rules Priority', formData?.rulesPriority, 'field-rules-priority')}
            {formData?.rulesCriteria !== 'Products' && renderField(
              'Multiplier Value',
              formData?.multiplierValue ? `${formData?.multiplierValue}x` : '',
              'field-multiplier-value'
            )}
          </div>
        </div>

        {/* Availability */}
        <div className="flex flex-col gap-4" data-testid="section-availability">
          {renderSectionHeader('Availability', 'header-availability', null)}
          <Divider />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>{renderField('Start Date', formData?.activeStartDate, 'field-start-date')}</div>
            <div>{formData?.hasEndDate && renderField('End Date', formData?.activeEndDate, 'field-end-date')}</div>
            <div />
          </div>
        </div>
        {/* Products */}
        {formData?.rulesCriteria === 'Products' && (
          <div className="flex flex-col gap-4" data-testid="section-products">
            {renderSectionHeader('Products', 'header-products', null)}
            <Divider />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>{renderFieldDownload('Product List', fileName, 'field-product-list', handleDownload)}</div>
              {/* <div>{renderField('Uploaded', formData?.uploadOutcome?.totalCount ?
                `${formData?.uploadOutcome?.totalCount - (formData?.uploadOutcome?.nonExistentIds?.length || 0)}/${formData?.uploadOutcome?.totalCount}` :
                '-', 'field-uploaded-count')}</div>
              <div /> */}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
